// MobileShell — mobil-visning med bunn-nav + kompakt topp-header.
// Aktiveres på skjermer < 768px OG som PWA. Skjuler web-header/footer.
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Home, MessageCircleMore, Image, Users, Star,
  Search, Bell, LogOut, Settings, Mail, Trophy,
  Video, ShoppingBag, User, Zap, MessageCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

// Base-nav som er like for alle. Siste tab er rolle-avhengig
// (vanlige: Skapere; skapere: Skaperprofil).
function buildBottomNav(user) {
  const isCreator = !!user?.is_creator;
  const myUsername = user?.username || "";
  return [
    { to: "/",       label: "Hjem",   icon: Home },
    { to: "/sok",    label: "Søk",    icon: Search },
    { to: "/chat",   label: "Chat",   icon: MessageCircle },
    { to: "/glimt",  label: "Glimt",  icon: Zap, accent: true },
    { to: myUsername ? `/profile/${myUsername}` : "/profile",
      label: "Profil", icon: User },
    isCreator
      ? { to: myUsername ? `/skaper/${myUsername}` : "/skapere",
          label: "Skaperprofil", icon: Star }
      : { to: "/skapere", label: "Skapere", icon: Star },
  ];
}

const AVATAR_MENU_ALL = [
  { key: "profile", label: "Min profil", icon: User, dynamic: true },
  { to: "/meldinger", label: "Meldinger", icon: Mail },
  { key: "creator-page", label: "Skaperprofil", icon: Star, creatorOnly: true, dynamic: true },
  { to: "/meldinger?context=creator", label: "Skaper-innboks", icon: MessageCircleMore, creatorOnly: true },
  { to: "/forum", label: "Forum", icon: MessageCircleMore },
  { to: "/bilder", label: "Galleri", icon: Image },
  { to: "/chat", label: "Felleschat", icon: MessageCircleMore },
  { to: "/noveller", label: "Noveller", icon: MessageCircleMore },
  { to: "/annonser", label: "Annonser", icon: ShoppingBag },
  { to: "/konkurranser", label: "Konkurranser", icon: Trophy },
  { to: "/online", label: "Online", icon: Video },
  { to: "/innstillinger", label: "Innstillinger", icon: Settings },
];

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function MobileShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const BOTTOM_NAV = buildBottomNav(user);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
    };
  }, []);

  const initials = (user?.username || user?.name || "?")[0].toUpperCase();

  const handleLogout = () => {
    setMenuOpen(false);
    logout?.();
    navigate("/logg-inn");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--noir)] text-[var(--cream)]" data-testid="mobile-shell">
      {/* Topp-header — kompakt */}
      <header className="sticky top-0 z-30 bg-[var(--noir)] border-b border-white/10 px-3 py-2.5 flex items-center gap-2 backdrop-blur">
        <Link to="/" className="font-editorial text-lg text-[var(--magenta)] tracking-tight" data-testid="mobile-logo">
          NE
        </Link>
        <div className="flex-1" />
        <div className="mr-1" data-testid="mobile-lang-switcher">
          <LanguageSwitcher variant="header" />
        </div>
        <Link to="/varsler" aria-label="Varsler" className="text-[var(--cream)] hover:text-[var(--magenta)] p-1.5 relative" data-testid="mobile-notifications">
          <Bell className="w-5 h-5" />
        </Link>
        {user ? (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-8 h-8 rounded-full bg-[var(--magenta)] text-white flex items-center justify-center text-xs font-bold"
              data-testid="mobile-avatar"
              aria-label="Meny"
            >
              {initials}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 min-w-[220px] bg-[var(--noir)] border border-white/10 shadow-xl z-40">
                <div className="px-3 py-2 border-b border-white/10">
                  <div className="text-xs text-[var(--cream-faint)]">Innlogget som</div>
                  <div className="font-meta text-sm text-[var(--cream)] truncate">{user.username || user.name}</div>
                </div>
                {AVATAR_MENU_ALL
                  .filter((m) => !m.creatorOnly || user.is_creator)
                  .map((m) => {
                    let to = m.to;
                    if (m.dynamic && m.key === "profile") to = `/profile/${user.username}`;
                    if (m.dynamic && m.key === "creator-page") to = `/min-skaperside`;
                    return (
                      <Link
                        key={m.key || m.to}
                        to={to}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--cream)] hover:bg-white/5 transition"
                        data-testid={`mobile-menu-${(m.key || m.to.slice(1)).replace(/[^a-z0-9-]/gi, "-")}`}
                      >
                        <m.icon className="w-4 h-4 text-[var(--cream-soft)]" />
                        {m.label}
                      </Link>
                    );
                  })}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition border-t border-white/10"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-4 h-4" />
                  {t("common.logout", "Logg ut")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/logg-inn" className="text-xs font-meta tracking-[0.1em] px-3 py-1.5 border border-[var(--magenta)] text-[var(--magenta)]" data-testid="mobile-login">
            LOGG INN
          </Link>
        )}
      </header>

      {/* Hovedinnhold — padding-bunn så bunn-nav ikke dekker */}
      <main className="flex-1 pb-16" data-testid="mobile-main">
        {children}
      </main>

      {/* Bunn-nav — alltid synlig */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-[var(--noir)] border-t border-white/10 flex items-stretch justify-around backdrop-blur"
        data-testid="mobile-bottom-nav"
      >
        {BOTTOM_NAV.map((n) => (
          <NavLink
            key={n.to + n.label}
            to={n.to}
            end={n.to === "/"}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition ${
                n.accent
                  ? "text-[var(--magenta)]"
                  : isActive ? "text-[var(--magenta)]" : "text-[var(--cream-soft)]"
              }`
            }
            data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
          >
            {n.accent ? (
              <div className="w-8 h-8 rounded-full border-2 border-[var(--magenta)] flex items-center justify-center -mt-1">
                <n.icon className="w-4 h-4" />
              </div>
            ) : (
              <n.icon className="w-5 h-5" />
            )}
            <span className="text-[10px] font-meta tracking-wider">{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
